import { FeatureFlag } from "@coeff/api";
import { DomainSettings } from "@coeff/api-admin-ext";
import { useFeatureGate } from "@statsig/react-bindings";
import { message } from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { useAdminExtSettings } from "../../../api";
import { Form, List, LoaderWithPerfTimings, CaretRightBlack } from "../../../components";
import { COLORS } from "../../../constants";
import { AppState } from "../../../store";
import { pluralize, useTrack } from "../../../utils";

import { AdminSettingsConfigureModal, SettingsType } from "./AdminSettingsConfigureModal";
import { DomainEnforcementSetting } from "./DomainEnforcementSetting";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 600px;
`;

const { Item } = List;

const StyledItem = styled(Item)`
  && {
    background: ${COLORS.black2};
    border: none;
    cursor: pointer;
    position: relative;
    margin-bottom: 10px;
    padding: 4px 20px 4px 12px;

    &:hover,
    &:focus-within,
    &:focus,
    &:active {
      background-color: ${COLORS.black4};

      .actionswrap {
        visibility: visible;
      }
    }

    .actionswrap {
      visibility: hidden;
    }

    .ant-list-item {
      background-color: ${COLORS.black4} !important;
      cursor: default !important;

      .actionswrap {
        visibility: visible !important;
        border-radius: 6px !important;
      }
    }

    .ant-list-item-meta {
      align-items: center;
    }

    .ant-list-item-meta-title {
      color: ${COLORS.black85};
      font-weight: bold;

      h4 {
        margin: 0px;
        overflow-wrap: break-word;
        word-wrap: break-word;
        word-break: break-word;
      }
    }

    .ant-list-item-meta-description {
      color: ${COLORS.black45};
      font-size: 12px;
      overflow-wrap: break-word;
      word-wrap: break-word;
      word-break: break-word;
    }
  }
`;

const Container = styled.div`
  max-width: 660px;
`;

const FormGroup = styled.div`
  h3 {
    font-weight: bold;
    margin: 0px 12px 8px 2px;
  }

  margin-top: 12px;
`;

const StyledSummaryText = styled.div`
  margin-right: 12px;
  color: ${COLORS.black45};
  text-align: right;
`;

const getconfigSummaryText = (
  settingsType: SettingsType,
  adminExtSettings: DomainSettings | undefined,
  isSingleDomain: boolean,
  showConnectorRestrictions?: boolean
) => {
  if (!adminExtSettings) {
    return "";
  }

  switch (settingsType) {
    case "writeback_settings": {
      const writebackSettings = adminExtSettings?.domain_level_writeback_settings;

      if (Boolean(writebackSettings?.is_disabled)) {
        return "Disabled";
      }

      const baseSummary = writebackSettings?.domains_list
        ? `${writebackSettings.domains_list.length} domain${pluralize(
            writebackSettings.domains_list.length
          )} ${writebackSettings.allow_or_disallow_domains === "allow" ? "allowed" : "not allowed"}`
        : writebackSettings?.user_emails_list
        ? `${writebackSettings.user_emails_list.length} user${pluralize(
            writebackSettings.user_emails_list.length
          )} ${writebackSettings.allow_or_disallow_users === "allow" ? "allowed" : "not allowed"}`
        : isSingleDomain
        ? "All users allowed"
        : "All domains and users allowed";

      return showConnectorRestrictions && writebackSettings?.allowed_connector_types?.length ? (
        <>
          <div>{baseSummary}</div>
          <div>
            {`Exports allowed for ${
              writebackSettings.allowed_connector_types.length
            } connector${pluralize(writebackSettings.allowed_connector_types.length)}`}
          </div>
        </>
      ) : (
        baseSummary
      );
    }
    case "slack_or_email_settings":
      return Boolean(adminExtSettings?.slack_or_email_alerts_settings?.is_disabled)
        ? "Disabled"
        : adminExtSettings?.slack_or_email_alerts_settings?.domains_list
        ? `${adminExtSettings.slack_or_email_alerts_settings.domains_list.length} domain${pluralize(
            adminExtSettings?.slack_or_email_alerts_settings?.domains_list.length
          )} ${
            adminExtSettings.slack_or_email_alerts_settings.allow_or_disallow_domains === "allow"
              ? "allowed"
              : "not allowed"
          }`
        : isSingleDomain
        ? "Enabled"
        : "All domains allowed";
    case "gpt_settings":
      return Boolean(adminExtSettings?.gpt_features_settings?.is_disabled)
        ? "Disabled"
        : adminExtSettings?.gpt_features_settings?.domains_list
        ? `${adminExtSettings.gpt_features_settings.domains_list.length} domain${pluralize(
            adminExtSettings?.gpt_features_settings?.domains_list.length
          )} ${
            adminExtSettings.gpt_features_settings.allow_or_disallow_domains === "allow"
              ? "allowed"
              : "not allowed"
          }`
        : adminExtSettings?.gpt_features_settings?.user_emails_list
        ? `${adminExtSettings.gpt_features_settings.user_emails_list.length} user${pluralize(
            adminExtSettings?.gpt_features_settings?.user_emails_list.length
          )} ${
            adminExtSettings.gpt_features_settings.allow_or_disallow_users === "allow"
              ? "allowed"
              : "not allowed"
          }`
        : isSingleDomain
        ? "Enabled"
        : "All domains allowed";
    case "cloud_table_settings":
      return Boolean(adminExtSettings?.cloud_table_settings?.is_disabled)
        ? "Disabled"
        : adminExtSettings?.cloud_table_settings?.domains_list
        ? `${adminExtSettings.cloud_table_settings.domains_list.length} domain${pluralize(
            adminExtSettings?.cloud_table_settings?.domains_list.length
          )} ${
            adminExtSettings.cloud_table_settings.allow_or_disallow_domains === "allow"
              ? "allowed"
              : "not allowed"
          }`
        : isSingleDomain
        ? "Enabled"
        : "All domains allowed";
    case "connection_sharing_settings":
      return Boolean(adminExtSettings?.connection_sharing_settings?.is_disabled)
        ? "Disabled"
        : "Enabled";
    case "allowed_data_sources_settings":
      return adminExtSettings?.allowed_data_sources_settings?.data_sources_list
        ? `${
            adminExtSettings.allowed_data_sources_settings.data_sources_list.length
          } data source${pluralize(
            adminExtSettings?.allowed_data_sources_settings?.data_sources_list.length
          )} ${
            adminExtSettings.allowed_data_sources_settings.allow_or_disallow_data_sources ===
            "allow"
              ? "allowed"
              : "not allowed"
          }`
        : "All data sources allowed";
    default:
      return "";
  }
};

export const AdminSettings: React.FC<{ domain?: string }> = ({ domain }) => {
  const track = useTrack();

  const [showAdminSettingsConfigureModal, setShowAdminSettingsConfigureModal] = useState(false);

  const [currentSettingType, setCurrentSettingType] = useState<SettingsType>();

  const [_, contextHolder] = message.useMessage();

  const history = useHistory();

  const location = useLocation();

  const { value: disableAllAiFeatures } = useFeatureGate(FeatureFlag.DisableAllAiFeatures);

  const { value: enableFreeDomainEnforcementAdmin } = useFeatureGate(
    FeatureFlag.EnableFreeDomainEnforcementAdmin
  );

  const { clientFlags: clientFlags, domains_in_domain_family: domains_in_domain_family } =
    useSelector((state: AppState) => ({
      domains_in_domain_family: state.app.domains_in_domain_family,
      clientFlags: state.app.clientFlags,
    }));

  const { data: adminExtSettings, isLoading: isLoadingAdminExtSettings } = useAdminExtSettings({
    enabled: Boolean(clientFlags?.is_gsuite_domain_admin),
  });

  const renderContent = (children: React.ReactNode) => {
    return (
      <Container>
        {contextHolder}
        {children}
      </Container>
    );
  };

  if (isLoadingAdminExtSettings) {
    return renderContent(<LoaderWithPerfTimings name="WorkspaceAdminSettings" />);
  }

  // For Enabling SSO config for single domains, we added them to a domain family with a dummy domain
  // that starts with "coeff." and is not a real domain.
  const isSingleDomain =
    domains_in_domain_family.filter(domain => !domain.toLowerCase().startsWith("coeff.")).length <
    2;

  // Superadmin-only free-domain enforcement. Scoped to the domain being inspected (passed by
  // WorkspaceAdmin; falls back to the URL param) and gated on is_coeff_admin so real domain admins
  // never see it. Prefer the prop over the URL so it survives SPA tab navigation, where the tab
  // Link briefly drops the ?domain= query string.
  const enforcementDomain = domain ?? new URLSearchParams(location.search).get("domain");
  const showEnforcement =
    Boolean(clientFlags?.is_coeff_admin) &&
    enableFreeDomainEnforcementAdmin &&
    Boolean(enforcementDomain) &&
    enforcementDomain !== "all_domains";

  return renderContent(
    <Content>
      <Form>
        <FormGroup>
          <h3>Features</h3>
        </FormGroup>
      </Form>
      {Boolean(adminExtSettings?.show_writeback_settings_in_ui) && (
        <StyledItem
          onClick={() => {
            setCurrentSettingType("writeback_settings");
            setShowAdminSettingsConfigureModal(true);
          }}
        >
          <Item.Meta
            title="Exports"
            description="Allow users to export data from Sheets to other systems"
          />
          <StyledSummaryText>
            {getconfigSummaryText(
              "writeback_settings",
              adminExtSettings,
              isSingleDomain,
              Boolean(clientFlags?.enable_writeback_connector_restrictions)
            )}
          </StyledSummaryText>
          <CaretRightBlack />
        </StyledItem>
      )}

      {Boolean(adminExtSettings?.show_alerts_settings_in_ui) && (
        <StyledItem
          onClick={() => {
            setCurrentSettingType("slack_or_email_settings");
            setShowAdminSettingsConfigureModal(true);
          }}
        >
          <Item.Meta
            title="Slack or email alerts"
            description="Allow users to create Slack or email alerts"
          />
          <StyledSummaryText>
            {getconfigSummaryText("slack_or_email_settings", adminExtSettings, isSingleDomain)}
          </StyledSummaryText>
          <CaretRightBlack />
        </StyledItem>
      )}

      {!disableAllAiFeatures && (
        <StyledItem
          onClick={() => {
            setCurrentSettingType("gpt_settings");
            setShowAdminSettingsConfigureModal(true);
          }}
        >
          <Item.Meta
            title="AI features"
            description="Allow users to access AI features powered by third-party AI providers"
          />
          <StyledSummaryText>
            {getconfigSummaryText("gpt_settings", adminExtSettings, isSingleDomain)}
          </StyledSummaryText>
          <CaretRightBlack />
        </StyledItem>
      )}

      <StyledItem
        onClick={() => {
          setCurrentSettingType("cloud_table_settings");
          setShowAdminSettingsConfigureModal(true);
        }}
      >
        <Item.Meta
          title="Cloud datasets"
          description="Allow users to save spreadsheet data to Coefficient's secure cloud"
        />
        <StyledSummaryText>
          {getconfigSummaryText("cloud_table_settings", adminExtSettings, isSingleDomain)}
        </StyledSummaryText>
        <CaretRightBlack />
      </StyledItem>

      <Form>
        <FormGroup>
          <h3>Data sources</h3>
        </FormGroup>
      </Form>

      <StyledItem
        onClick={() => {
          setCurrentSettingType("connection_sharing_settings");
          setShowAdminSettingsConfigureModal(true);
        }}
      >
        <Item.Meta
          title="Connection sharing"
          description="Allow users to share connections with other users in your domain(s)"
        />
        <StyledSummaryText>
          {getconfigSummaryText("connection_sharing_settings", adminExtSettings, isSingleDomain)}
        </StyledSummaryText>
        <CaretRightBlack />
      </StyledItem>

      {Boolean(adminExtSettings?.show_allowed_data_source_settings_in_ui) && (
        <StyledItem
          onClick={() => {
            setCurrentSettingType("allowed_data_sources_settings");
            setShowAdminSettingsConfigureModal(true);
          }}
        >
          <Item.Meta
            title="Allowed data sources"
            description="Allow users to only connect to specified data sources"
          />
          <StyledSummaryText>
            {getconfigSummaryText(
              "allowed_data_sources_settings",
              adminExtSettings,
              isSingleDomain
            )}
          </StyledSummaryText>
          <CaretRightBlack />
        </StyledItem>
      )}

      <StyledItem
        onClick={() => {
          history.push({ pathname: `/dashboard/settings/oauth-settings` });
        }}
      >
        <Item.Meta
          title="OAuth settings"
          description="Allow users to connect to Looker, NetSuite & Snowflake using OAuth"
        />
        <StyledSummaryText>Manage</StyledSummaryText>
        <CaretRightBlack />
      </StyledItem>

      {Boolean(clientFlags?.show_sso_config_ui) && (
        <>
          <Form>
            <FormGroup>
              <h3>Login & security</h3>
            </FormGroup>
          </Form>

          <StyledItem
            onClick={() => {
              history.push({ pathname: `/dashboard/settings/configure-sso` });
            }}
          >
            <Item.Meta
              title="SSO authentication"
              description="Requires users to sign in with your SSO provider before using Coefficient"
            />
            <StyledSummaryText>Configure</StyledSummaryText>
            <CaretRightBlack />
          </StyledItem>
        </>
      )}

      {showEnforcement && enforcementDomain && (
        <>
          <Form>
            <FormGroup>
              <h3>Internal (Coefficient admins)</h3>
            </FormGroup>
          </Form>
          <DomainEnforcementSetting domain={enforcementDomain} />
        </>
      )}

      <AdminSettingsConfigureModal
        visible={showAdminSettingsConfigureModal}
        onSuccess={() => message.success("asda")}
        onCancel={() => setShowAdminSettingsConfigureModal(false)}
        settingsType={currentSettingType}
      />
    </Content>
  );
};
